// Lib
import React, { CSSProperties, RefObject } from 'react';

// Utils
import {
    getBackgroundColor,
    getCaption,
    getElementId,
    getImageThumbnail,
    getIsTransparentImage,
    getShowCaption,
} from '../../../../../../common/elements/utils/elementPropertyUtils';
import { toCssUrl } from '../../../../../utils/cssUtil';
import { getColorCssValue } from '../../../../utils/elementColorStyleUtils';

// Components
import Caption from '../../../../../components/caption/Caption';
import ElementImage from '../../../../../components/images/ElementImage';
import ImageDrawing from '../../../ImageDrawing';

// Constants
import { IMAGE_TYPES } from '../../../../../../common/media/mediaConstants';

import { ImMNElement } from '../../../../../../common/elements/elementModelTypes';

type ImageModalV2ContentProps = {
    element: ImMNElement;
    lowerQualityImageUrl?: string;
    isEditable: boolean;
    contentStyle: CSSProperties;
    imageWrapperStyle: CSSProperties;
    captionRef: RefObject<HTMLDivElement>;
};

/**
 * Renders the image filling the available space while maintaining aspect ratio.
 */
const ImageModalV2Content: React.FC<ImageModalV2ContentProps> = ({
    element,
    lowerQualityImageUrl,
    isEditable,
    contentStyle,
    imageWrapperStyle,
    captionRef,
}) => {
    const elementId = getElementId(element);
    const isTransparent = getIsTransparentImage(element);
    const backgroundColorCss = isTransparent ? getColorCssValue(getBackgroundColor(element)) : null;
    const imageThumbnailUrl = getImageThumbnail(element);
    const showCaption = getShowCaption(element);
    const imageBackgroundStyle =
        !isTransparent && imageThumbnailUrl ? { backgroundImage: toCssUrl(imageThumbnailUrl) } : undefined;

    return (
        <div className="ImageModalV2Content flex h-full w-full items-center justify-center">
            <div
                className="ImageModalV2Content-image-wrapper"
                style={{
                    ...contentStyle,
                    ...(backgroundColorCss && { '--ws-element-background-primary': backgroundColorCss }),
                }}
            >
                <div
                    className="ImageModalV2Content-image"
                    style={{
                        ...imageBackgroundStyle,
                        ...imageWrapperStyle,
                    }}
                >
                    <ElementImage
                        useSecureMediaUrl
                        showBrokenIconOnError
                        element={element}
                        imageType={IMAGE_TYPES.ELEMENT}
                        widthPx={Number(imageWrapperStyle.width)}
                        thumbnailUrl={lowerQualityImageUrl}
                        displayFullSize
                        key={elementId}
                    />
                    <ImageDrawing element={element} />
                </div>
                {showCaption && (
                    <div ref={captionRef} className="ImageModalV2Content-caption flex-none">
                        <Caption
                            element={element}
                            textContent={getCaption(element)}
                            captionVisible={showCaption}
                            isEditable={isEditable}
                            isModalView
                        />
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageModalV2Content;
